// src/pages/Lounge/SearchClubsCity/SearchClubsCity.jsx
import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion as Motion } from "framer-motion";
import Loading from "@/components/Loading/Loading";

import MrNoty from "@/assets/UI/Mr.webp";
import MrsNoty from "@/assets/UI/Mrs.webp";
import CityImage from "@/assets/UI/city.webp";

import api from "@/utils/axios";

// Reuse the same SCSS module as SearchClubs
import cls from "../SearchClubs/SearchClubs.module.scss";

const floatSlow = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: [0.5, 0.65, 0.5],
    scale: [1, 1.04, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: [1, 0.62, 1],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const slideInUp = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 220, damping: 24, mass: 0.9 },
  },
};

const SearchClubsCity = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { cityId } = useParams();

  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  useEffect(() => {
    if (!cityId) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const res = await api.get(`/v1/lounge/cities/search/${cityId}`);
        if (cancelled) return;
        setCity(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              t(
                "searchClubsCity.errors.loadFailed",
                "Unable to load this city.",
              ),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cityId, t]);

  const streets = city?.streets || [];

  const mrSrc = city?.mrNotyImage?.url || MrNoty;
  const mrsSrc = city?.mrsNotyImage?.url || MrsNoty;

  // ✅ Loader gate AFTER hooks (safe)
  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  // Error / not found
  if (error || !city) {
    return (
      <div className={cls.page}>
        <div className={cls.pageInner}>
          {/* Tabs row */}
          <nav className={cls.tabs}>
            <Link
              to="/lounge"
              className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
            >
              {t("memberPublicFeed.tabs.public")}
            </Link>
            <Link
              to="/lounge/friends-feed"
              className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
            >
              {t("memberPublicFeed.tabs.friends")}
            </Link>
            <Link
              to="/lounge/clubs-feed"
              className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
            >
              {t("memberPublicFeed.tabs.clubs")}
            </Link>
            <Link
              to="/lounge/chat"
              className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
            >
              {t("memberPublicFeed.tabs.chat")}
            </Link>
            <Link
              to="/lounge/search"
              className={`${cls.tabButton} ${
                isSearch ? `${cls.activeTab} ${cls.activeSearchTab}` : ""
              }`}
            >
              🔍 {t("memberPublicFeed.tabs.search")}
            </Link>
          </nav>

          <section className={cls.card}>
            <p className={cls.cityListMeta}>
              {error || t("searchClubsCity.errors.notFound", "City not found.")}
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${
              isSearch ? `${cls.activeTab} ${cls.activeSearchTab}` : ""
            }`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        {/* Main layout – single centered column */}
        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* Clubs CTA – compact 3-button row (same as SearchClubs) */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("memberClubsFeed.title")}</h2>
              </div>

              <div className={cls.clubActionsRow}>
                {/* My Clubs */}
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                {/* Explore */}
                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.19 6.19l-2.47 7.4c-.1.31-.49.31-.59 0l-2.47-7.4a.5.5 0 01.63-.63l7.4 2.47c.31.1.31.49 0 .59l-7.4 2.47a.5.5 0 01-.63-.63l2.47-7.4c.1-.31.49-.31.59 0l2.47 7.4a.5.5 0 01-.63.63l-7.4-2.47c-.31-.1-.31-.49 0-.59l7.4-2.47a.5.5 0 01.63.63z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                {/* Search */}
                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16a6.471 6.471 0 004.23-1.57l.27.28v.79l5 5L20.49 19l-5-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            {/* City hero */}
            <section className={cls.searchHero}>
              <div className={cls.searchHeroStage}>
                {/* BACK: Mr & Mrs art */}
                <Motion.div
                  className={`${cls.heroFrame} ${cls.heroArtFrame}`}
                  variants={slideInUp}
                  initial="hidden"
                  animate="show"
                  aria-hidden="true"
                >
                  <div className={cls.heroArtWrap}>
                    <Motion.img
                      src={mrSrc}
                      alt=""
                      className={`${cls.heroArt} ${cls.heroMr}`}
                      variants={floatSlow}
                      initial="hidden"
                      animate="show"
                    />
                    <Motion.img
                      src={mrsSrc}
                      alt=""
                      className={`${cls.heroArt} ${cls.heroMrs}`}
                      variants={floatSlowAlt}
                      initial="hidden"
                      animate="show"
                      transition={{ delay: 0.25 }}
                    />
                  </div>
                </Motion.div>

                {/* FRONT: heroCard */}
                <Motion.div
                  className={`${cls.heroFrame} ${cls.heroCard}`}
                  variants={slideInUp}
                  initial="hidden"
                  animate="show"
                >
                  <span className={cls.searchPromptLabel}>
                    {t("searchClubsCity.hero.label")}
                  </span>
                  <h2 className={cls.searchPromptTitle}>{city.cityName}</h2>
                  <p className={cls.searchPromptText}>
                    {city.cityDescription || ""}
                  </p>
                </Motion.div>
              </div>
            </section>

            {/* Streets list */}
            <section className={cls.cityListSection}>
              <div className={cls.cityListHeader}>
                <h3 className={cls.cityListTitle}>
                  {t("searchClubsCity.roads.title", {
                    cityName: city.cityName,
                  })}
                </h3>
                <p className={cls.cityListMeta}>
                  {t("searchClubsCity.roads.meta")}
                </p>
              </div>

              <div className={cls.cityList}>
                {streets.length === 0 ? (
                  <p className={cls.cityListEmpty}>
                    {t("searchClubsCity.roads.empty")}
                  </p>
                ) : (
                  streets.map((street) => (
                    <Motion.article
                      key={street.streetId}
                      className={cls.cityCard}
                      whileHover={{ y: -2, scale: 1.01 }}
                      transition={{ duration: 0.14 }}
                    >
                      <div className={cls.cityThumb}>
                        <img
                          src={street.avatarThumb?.url || CityImage}
                          alt={street.streetName}
                          className={cls.cityThumbImg}
                        />
                      </div>

                      <div className={cls.cityContent}>
                        <p className={cls.cityVibeText}>
                          <strong>{street.streetName}</strong>
                          {street.streetVibe ? ` — ${street.streetVibe}` : ""}
                        </p>

                        <Link
                          to={`/lounge/search-clubs/city/${city.cityId}/${street.streetId}`}
                          className={cls.cityButton}
                        >
                          {t("searchClubs.cityCard.button")}
                        </Link>
                      </div>
                    </Motion.article>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchClubsCity;
